import { useEffect, useReducer } from 'react';

import { getEffectiveOrgModuleSlugs, readNormalizedUserFromStorage } from '../utils/entitlements';
import { SESSION_CONTINUED_EVENT } from '../services/auth/sessionExpiry';
import { getModuleCatalogState, loadModuleCatalog, subscribeModuleCatalog } from './moduleCatalog';
import { makeModuleLicense } from './moduleLicense';

/**
 * The signed-in tenant's module licence, read now (no React).
 *
 * Slugs come from the JWT `modules` claim (re-issued on every token refresh),
 * falling back to localStorage user.modules — getEffectiveOrgModuleSlugs().
 */
export function getModuleLicense() {
  const catalog = getModuleCatalogState();
  let token = null;
  try { token = localStorage.getItem('jwt_token'); } catch { /* no storage */ }
  return makeModuleLicense({
    index: catalog.index,
    lockEnabled: catalog.lockEnabled,
    ready: catalog.ready,
    slugs: getEffectiveOrgModuleSlugs(),
    user: readNormalizedUserFromStorage(),
    token,
  });
}

/**
 * The same, as a hook: re-renders when the catalog lands, when another tab
 * changes the signed-in user, and when the session's token is refreshed (a
 * super admin may have changed the org's modules).
 */
export default function useModuleLicense() {
  const [, bump] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    const unsubscribe = subscribeModuleCatalog(bump);
    loadModuleCatalog();
    const onStorage = (e) => { if (!e || e.key === 'user' || e.key === 'jwt_token') bump(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SESSION_CONTINUED_EVENT, bump);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SESSION_CONTINUED_EVENT, bump);
    };
  }, []);

  return getModuleLicense();
}
